import type { ImageFolderCategory, ImageFolderFilters } from "@shared/api";

type FilterArrayKey = "panelStructure" | "brand" | "folderType" | "model" | "color" | "prelisType" | "category";

const FILTER_ARRAY_KEYS: FilterArrayKey[] = [
  "panelStructure",
  "brand",
  "folderType",
  "model",
  "color",
  "prelisType",
  "category",
];

function readArrayParam(params: URLSearchParams, key: string): string[] | undefined {
  const values = params.getAll(key).map((v) => v.trim()).filter(Boolean);
  return values.length > 0 ? values : undefined;
}

function toMaybeArray(values: string[] | undefined): string | string[] | undefined {
  if (!values || values.length === 0) return undefined;
  return values.length === 1 ? values[0] : values;
}

export function readImageFiltersFromQuery(params: URLSearchParams): {
  filters: ImageFolderFilters;
  searchName: string;
  dateFilterEnabled: boolean;
} {
  const filters: ImageFolderFilters = {};
  for (const key of FILTER_ARRAY_KEYS) {
    const values = readArrayParam(params, key);
    if (!values) continue;
    if (key === "category") {
      filters.category = toMaybeArray(values) as ImageFolderCategory | ImageFolderCategory[] | undefined;
    } else {
      (filters as Record<string, unknown>)[key] = toMaybeArray(values);
    }
  }

  const dateFrom = params.get("dateFrom")?.trim();
  const dateTo = params.get("dateTo")?.trim();
  const searchName = params.get("name")?.trim() ?? "";
  const dateFilterEnabled = params.get("dateFilter") === "1";

  if (dateFrom) filters.dateFrom = dateFrom;
  if (dateTo) filters.dateTo = dateTo;

  return { filters, searchName, dateFilterEnabled };
}

export function writeImageFiltersToQuery(
  params: URLSearchParams,
  payload: { filters: ImageFolderFilters; searchName: string; dateFilterEnabled: boolean }
): URLSearchParams {
  const next = new URLSearchParams(params);
  const { filters, searchName, dateFilterEnabled } = payload;

  for (const key of FILTER_ARRAY_KEYS) next.delete(key);
  next.delete("dateFrom");
  next.delete("dateTo");
  next.delete("name");
  next.delete("dateFilter");

  for (const key of FILTER_ARRAY_KEYS) {
    const raw = (filters as Record<string, unknown>)[key];
    if (!raw) continue;
    const values = Array.isArray(raw) ? raw : [raw];
    values
      .map((v) => String(v).trim())
      .filter(Boolean)
      .forEach((v) => next.append(key, v));
  }

  if (filters.dateFrom) next.set("dateFrom", filters.dateFrom);
  if (filters.dateTo) next.set("dateTo", filters.dateTo);
  if (searchName.trim()) next.set("name", searchName.trim());
  if (dateFilterEnabled) next.set("dateFilter", "1");

  return next;
}
